import React, { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Check, Copy, Download, FileText } from "lucide-react";
import { GuestTopBar } from "../components/GuestTopBar";
import { usePageMeta } from "../hooks/usePageMeta";
import { PRESS_KIT } from "../lib/press-kit.generated";
import "../layouts/appshell.css";

/**
 * Public media kit (/press). All copy and the asset index come from
 * lib/press-kit.generated.ts, which is written by
 * atlas-design-system/project/marketing/press/build.mjs from kit.json -
 * change the kit there, not here.
 */

// Logo tiles need a backing that does not follow the site theme: a white
// logo has to sit on ink whichever theme the visitor is using.
const INK = PRESS_KIT.brand.colours[1].hex;
const PAPER = PRESS_KIT.brand.colours[2].hex;

const COLOURWAY_LABELS: Record<string, string> = {
  "gold-gradient": "Gold gradient",
  gold: "Gold",
  white: "White",
  ink: "Ink",
};

const linkStyle = { color: "var(--text-accent)" } as const;

const cardStyle: React.CSSProperties = {
  background: "var(--surface-card)",
  border: "1px solid var(--border-subtle)",
  borderRadius: "var(--radius-lg)",
  boxShadow: "var(--shadow-sm), var(--ring-top)",
};

const fileLinkStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontSize: "var(--text-sm)",
  fontWeight: 500,
  color: "var(--text-accent)",
  textDecoration: "none",
};

// Handwriting is used for the photo annotations and the sign-off only.
const HAND_FONT = '"Caveat", "Bradley Hand", cursive';
const HAND_FONT_URL = "https://fonts.googleapis.com/css2?family=Caveat:wght@600;700&display=swap";

/**
 * Hand-drawn labels over the maker photo. Coordinates are in the cropped
 * photo's own pixels (founder.photoCrop in kit.json): re-place them if the
 * crop changes.
 */
function PhotoAnnotations({ width, height }: { width: number; height: number }) {
  const stroke = { fill: "none", stroke: "#fff", strokeWidth: 5, strokeLinecap: "round", strokeLinejoin: "round" } as const;
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", filter: "drop-shadow(0 1px 3px rgba(0, 0, 0, .45))" }}
    >
      <text x={118} y={172} transform="rotate(-8 118 172)" fill="#fff" style={{ fontFamily: HAND_FONT, fontSize: 92, fontWeight: 700 }}>
        me
      </text>
      <path d="M 226 146 C 272 112, 326 148, 352 215" {...stroke} />
      <path d="M 325 194 L 352 215 L 351 181" {...stroke} />

      <text x={508} y={112} transform="rotate(5 508 112)" fill="#fff" style={{ fontFamily: HAND_FONT, fontSize: 66, fontWeight: 700 }}>
        the view
      </text>
      <path d="M 652 142 C 690 205, 682 268, 640 320" {...stroke} />
      <path d="M 670 305 L 640 320 L 648 287" {...stroke} />
    </svg>
  );
}

function megabytes(bytes: number) {
  return `${(bytes / 1024 / 1024).toFixed(0)} MB`;
}

function Section({ id, eyebrow, title, intro, children }: {
  id: string; eyebrow: string; title: string; intro?: string; children: React.ReactNode;
}) {
  return (
    <section id={id} style={{ marginTop: 72, scrollMarginTop: 24 }}>
      <p className="eyebrow" style={{ marginBottom: 10 }}>{eyebrow}</p>
      <h2 style={{ font: "var(--type-h2)", color: "var(--text-primary)", margin: "0 0 12px" }}>{title}</h2>
      {intro && (
        <p style={{ color: "var(--text-secondary)", lineHeight: "var(--leading-relaxed)", margin: "0 0 24px", maxWidth: 680 }}>
          {intro}
        </p>
      )}
      {children}
    </section>
  );
}

function CopyBlock({ label, text }: { label: string; text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div style={{ ...cardStyle, padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 10 }}>
        <span className="eyebrow">{label}</span>
        <button
          type="button"
          onClick={copy}
          style={{ ...fileLinkStyle, background: "none", border: 0, padding: 0, cursor: "pointer", color: "var(--text-secondary)" }}
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      {text.split("\n\n").map((para) => (
        <p key={para} style={{ color: "var(--text-primary)", lineHeight: "var(--leading-relaxed)", margin: "0 0 10px" }}>
          {para}
        </p>
      ))}
    </div>
  );
}

function Gallery({ items, columns, mobileCols, note }: {
  items: readonly { file: string; caption: string; preview: string; width: number; height: number }[];
  columns: number;
  mobileCols: "r-cols-1" | "r-cols-2";
  note: string;
}) {
  return (
    <>
      <div className={mobileCols} style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: 16 }}>
        {items.map((item) => (
          <figure key={item.file} style={{ margin: 0, minWidth: 0 }}>
            <img
              src={item.preview}
              alt={item.caption}
              loading="lazy"
              width={item.width}
              height={item.height}
              style={{ display: "block", width: "100%", height: "auto", borderRadius: "var(--radius-md)", border: "1px solid var(--border-subtle)" }}
            />
            <figcaption style={{ color: "var(--text-muted)", fontSize: "var(--text-xs)", marginTop: 8, lineHeight: 1.4 }}>
              {item.caption}
            </figcaption>
          </figure>
        ))}
      </div>
      <p style={{ color: "var(--text-muted)", fontSize: "var(--text-sm)", marginTop: 16 }}>
        {note}{" "}
        <a href={PRESS_KIT.downloads.zip} download style={linkStyle}>Download the kit</a>.
      </p>
    </>
  );
}

export default function PressPage() {
  usePageMeta({
    title: "Press and media kit - Atlas",
    description: `${PRESS_KIT.boilerplate.oneLine} Logos, screenshots, photography and the facts, free to use for editorial coverage.`,
    canonicalPath: "/press",
  });

  const { boilerplate, founder, brand, downloads } = PRESS_KIT;

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-app)", display: "flex", flexDirection: "column" }}>
      <link rel="stylesheet" href={HAND_FONT_URL} precedence="default" />
      <GuestTopBar />
      <div className="r-page" style={{ flex: 1, padding: "56px 28px 96px" }}>
        <div style={{ maxWidth: 1040, margin: "0 auto" }}>

          <header>
            <p className="eyebrow" style={{ marginBottom: 12 }}>Press and media kit</p>
            <h1 style={{ font: "var(--type-display)", fontSize: "clamp(34px, 5vw, 52px)", color: "var(--text-primary)", margin: "0 0 36px", maxWidth: 720 }}>
              {founder.greeting}
            </h1>
            {/* Photo first in the DOM so it sits under the heading on a phone;
                row-reverse puts it on the right on wide screens. */}
            <div className="r-wrap" style={{ display: "flex", flexDirection: "row-reverse", gap: 48, alignItems: "flex-start" }}>
              <figure className="r-basis-full" style={{ flex: "0 0 38%", minWidth: 0, margin: 0 }}>
                <div
                  style={{
                    position: "relative", transform: "rotate(1.5deg)",
                    border: `10px solid ${PAPER}`, borderRadius: 6,
                    boxShadow: "var(--shadow-lg)", background: PAPER,
                  }}
                >
                  <img
                    src={founder.photoCrop}
                    alt={founder.photoCaption}
                    width={founder.photoCropSize.width}
                    height={founder.photoCropSize.height}
                    style={{ display: "block", width: "100%", height: "auto" }}
                  />
                  <PhotoAnnotations width={founder.photoCropSize.width} height={founder.photoCropSize.height} />
                </div>
                <figcaption style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 18 }}>
                  <a href={founder.photoCrop} download style={fileLinkStyle}>
                    <Download size={14} />
                    This photo, without the scribbles
                  </a>
                  <a href={founder.photo} download style={fileLinkStyle}>
                    <Download size={14} />
                    Uncropped
                  </a>
                </figcaption>
              </figure>
              <div className="r-basis-full" style={{ flex: 1, minWidth: 0 }}>
                {founder.story.map((para) => (
                  <p key={para} style={{ color: "var(--text-secondary)", fontSize: "var(--text-lg)", lineHeight: "var(--leading-relaxed)", margin: "0 0 18px" }}>
                    {para}
                  </p>
                ))}
                <p style={{ fontFamily: HAND_FONT, fontSize: 44, fontWeight: 700, lineHeight: 1, color: "var(--text-primary)", margin: "28px 0 0", transform: "rotate(-3deg)", transformOrigin: "left" }}>
                  {founder.signoff}
                </p>
                <p style={{ margin: "14px 0 0" }}>
                  <a href={`mailto:${PRESS_KIT.contact.email}`} style={linkStyle}>{PRESS_KIT.contact.email}</a>
                </p>
              </div>
            </div>
          </header>

          <section id="download" style={{ ...cardStyle, marginTop: 72, padding: 32, scrollMarginTop: 24 }}>
            <p className="eyebrow" style={{ marginBottom: 10 }}>The media kit</p>
            <h2 style={{ font: "var(--type-h2)", color: "var(--text-primary)", margin: "0 0 10px" }}>{PRESS_KIT.tagline}</h2>
            <p style={{ color: "var(--text-secondary)", lineHeight: "var(--leading-relaxed)", margin: "0 0 24px", maxWidth: 680 }}>
              {boilerplate.oneLine} Logos, screenshots, cover art and the facts, free to use for
              editorial coverage.
            </p>
            <div className="r-wrap" style={{ display: "flex", alignItems: "center", gap: 20 }}>
              <a
                href={downloads.zip}
                download
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  height: "var(--control-lg, 48px)", padding: "0 22px",
                  borderRadius: "var(--radius-md)", background: "var(--accent)",
                  color: "var(--text-on-gold)", fontWeight: 600, textDecoration: "none",
                }}
              >
                <Download size={18} />
                Download the media kit
              </a>
              <a href={downloads.factSheet} style={fileLinkStyle}>
                <FileText size={16} />
                Fact sheet (PDF)
              </a>
            </div>
            <p className="eyebrow" style={{ color: "var(--text-muted)", marginTop: 18 }}>
              ZIP · {megabytes(downloads.zipBytes)} · version {PRESS_KIT.version} · updated {PRESS_KIT.updated}
            </p>
          </section>

          <Section id="about" eyebrow="About" title="Atlas in your own word count">
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <CopyBlock label="One line" text={boilerplate.oneLine} />
              <CopyBlock label="Fifty words" text={boilerplate.short} />
              <CopyBlock label="In full" text={boilerplate.long.join("\n\n")} />
            </div>
          </Section>

          <Section id="facts" eyebrow="Fact sheet" title="The facts">
            <div style={{ ...cardStyle, padding: "4px 20px" }}>
              {PRESS_KIT.factSheet.map((fact, i) => (
                <div
                  key={fact.label}
                  className="r-wrap"
                  style={{
                    display: "flex", gap: 16, padding: "14px 0",
                    borderTop: i === 0 ? "none" : "1px solid var(--border-subtle)",
                  }}
                >
                  <span className="eyebrow r-basis-full" style={{ flex: "0 0 190px", paddingTop: 3 }}>{fact.label}</span>
                  <span className="tnum" style={{ flex: 1, minWidth: 0, color: "var(--text-primary)", lineHeight: "var(--leading-normal)" }}>
                    {fact.value}
                  </span>
                </div>
              ))}
            </div>
            <p style={{ color: "var(--text-muted)", fontSize: "var(--text-sm)", marginTop: 14 }}>
              Dataset figures as of {PRESS_KIT.factsAsOf}. Data sources are listed on
              the <Link to="/credits" style={linkStyle}>credits page</Link>.
            </p>
          </Section>

          <Section
            id="logos"
            eyebrow="Logos"
            title="The logo"
            intro="The mark is a set of nested contour chevrons: a summit, a contour map and the letter A. SVG for screens and print, PNG at 3,000 px for everything else."
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
              {PRESS_KIT.logos.map((group) => (
                <div key={group.id}>
                  <h3 style={{ font: "var(--type-label)", color: "var(--text-primary)", margin: "0 0 12px" }}>{group.label}</h3>
                  <div className="r-cols-2" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 16 }}>
                    {group.variants.map((v) => (
                      <div key={v.colour} style={{ minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex", alignItems: "center", justifyContent: "center",
                            aspectRatio: "3 / 2", padding: 24, borderRadius: "var(--radius-md)",
                            border: "1px solid var(--border-subtle)",
                            background: v.colour === "ink" ? PAPER : INK,
                          }}
                        >
                          <img src={v.svg} alt={`Atlas ${group.label.toLowerCase()}, ${COLOURWAY_LABELS[v.colour]?.toLowerCase()}`} loading="lazy" style={{ maxWidth: "100%", maxHeight: "100%" }} />
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 8 }}>
                          <span style={{ color: "var(--text-muted)", fontSize: "var(--text-xs)" }}>{COLOURWAY_LABELS[v.colour]}</span>
                          <span style={{ display: "flex", gap: 12 }}>
                            <a href={v.svg} download style={fileLinkStyle}>SVG</a>
                            <a href={v.png} download style={fileLinkStyle}>PNG</a>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 16, marginTop: 32 }}>
              <div style={{ ...cardStyle, padding: 20 }}>
                <p className="eyebrow" style={{ marginBottom: 12 }}>Do</p>
                <ul style={{ margin: 0, paddingLeft: 18, color: "var(--text-secondary)", lineHeight: "var(--leading-relaxed)" }}>
                  {brand.logoDo.map((line) => <li key={line}>{line}</li>)}
                </ul>
              </div>
              <div style={{ ...cardStyle, padding: 20 }}>
                <p className="eyebrow" style={{ marginBottom: 12 }}>Do not</p>
                <ul style={{ margin: 0, paddingLeft: 18, color: "var(--text-secondary)", lineHeight: "var(--leading-relaxed)" }}>
                  {brand.logoDont.map((line) => <li key={line}>{line}</li>)}
                </ul>
              </div>
            </div>
          </Section>

          <Section id="brand" eyebrow="Brand" title="Name, colour and type">
            <ul style={{ margin: "0 0 28px", paddingLeft: 18, color: "var(--text-secondary)", lineHeight: "var(--leading-relaxed)" }}>
              {brand.naming.map((line) => <li key={line}>{line}</li>)}
            </ul>
            <div className="r-cols-2" style={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: 16 }}>
              {brand.colours.map((c) => (
                <div key={c.name} style={{ minWidth: 0 }}>
                  <div style={{ height: 84, borderRadius: "var(--radius-md)", border: "1px solid var(--border-default)", background: c.hex }} />
                  <div style={{ color: "var(--text-primary)", fontWeight: 600, fontSize: "var(--text-sm)", marginTop: 10 }}>{c.name}</div>
                  <div className="eyebrow" style={{ marginTop: 2 }}>{c.hex}</div>
                  <div style={{ color: "var(--text-muted)", fontSize: "var(--text-xs)", marginTop: 6, lineHeight: 1.4 }}>{c.use}</div>
                </div>
              ))}
            </div>
            <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 16, marginTop: 28 }}>
              {brand.type.map((t, i) => (
                <div key={t.name} style={{ ...cardStyle, padding: 20, minWidth: 0 }}>
                  <div
                    style={{
                      fontFamily: ["var(--font-display)", "var(--font-sans)", "var(--font-mono)"][i],
                      fontSize: 24, fontWeight: i === 0 ? 700 : 400, color: "var(--text-primary)",
                    }}
                  >
                    {t.name}
                  </div>
                  <div style={{ color: "var(--text-muted)", fontSize: "var(--text-sm)", marginTop: 8 }}>{t.use}</div>
                </div>
              ))}
            </div>
          </Section>

          <Section id="screenshots" eyebrow="Screenshots" title="The app">
            <Gallery
              items={PRESS_KIT.screenshots}
              columns={4}
              mobileCols="r-cols-2"
              note="Full-resolution, uncaptioned screenshots are in the kit."
            />
          </Section>

          <Section id="key-art" eyebrow="Key art" title="Cover images">
            <Gallery
              items={PRESS_KIT.keyArt}
              columns={2}
              mobileCols="r-cols-1"
              note="Full-resolution covers, the app icon and a set of photographs from the hills are in the kit."
            />
          </Section>

          <Section id="usage" eyebrow="Usage" title="Using these assets">
            <div style={{ maxWidth: 680, display: "flex", flexDirection: "column", gap: 12 }}>
              {PRESS_KIT.usage.map((line) => (
                <p key={line} style={{ color: "var(--text-secondary)", lineHeight: "var(--leading-relaxed)", margin: 0 }}>{line}</p>
              ))}
            </div>
          </Section>

          <Section id="contact" eyebrow="Contact" title="Press enquiries">
            <p style={{ color: "var(--text-secondary)", lineHeight: "var(--leading-relaxed)", margin: 0, maxWidth: 680 }}>
              {PRESS_KIT.contact.name}:{" "}
              <a href={`mailto:${PRESS_KIT.contact.email}`} style={linkStyle}>{PRESS_KIT.contact.email}</a>.
              Happy to answer questions, including the detailed ones about how the matching works.
            </p>
          </Section>

          <footer style={{ display: "flex", gap: 22, flexWrap: "wrap", marginTop: 72, paddingTop: 24, borderTop: "1px solid var(--border-subtle)", fontSize: "var(--text-sm)" }}>
            <Link to="/" style={{ color: "var(--text-muted)", textDecoration: "none" }}>goatlas.co.uk</Link>
            <Link to="/privacy" style={{ color: "var(--text-muted)", textDecoration: "none" }}>Privacy</Link>
            <Link to="/terms" style={{ color: "var(--text-muted)", textDecoration: "none" }}>Terms</Link>
            <Link to="/credits" style={{ color: "var(--text-muted)", textDecoration: "none" }}>Credits</Link>
          </footer>
        </div>
      </div>
    </div>
  );
}
